/**
 * A reminder to add a second passkey.
 *
 * A passkey is the only credential of this service. A person with one passkey
 * has one way in, and a lost device then needs the operator. The reminder says
 * so once, and again after a while. It blocks nothing and it never repeats
 * while the person is working.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Button, buttonClass } from "./primitives.tsx";

/** How long a dismissal lasts. */
const QUIET_DAYS = 14;
const KEY = "arcgrc.passkey-reminder.dismissed";

function dismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) {
      return false;
    }
    return Date.now() - Number(raw) < QUIET_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // A browser that refuses storage still sees the reminder. It loses the
    // dismissal only.
    return false;
  }
}

function remember(): void {
  try {
    window.localStorage.setItem(KEY, String(Date.now()));
  } catch {
    // The dismissal is a convenience.
  }
}

export function PasskeyReminder({ accountUrl }: { readonly accountUrl: string }) {
  const [dismissed, setDismissed] = useState(dismissedRecently);

  const credentials = useQuery({
    queryKey: ["session-credentials"],
    queryFn: async () => {
      const answer = await fetch("/api/session/credentials", {
        credentials: "same-origin",
      });
      if (!answer.ok) {
        return { passkeys: null as number | null };
      }
      return (await answer.json()) as { passkeys: number | null };
    },
    retry: false,
    staleTime: 60 * 60 * 1000,
  });

  const count = credentials.data?.passkeys ?? null;
  // Null means the server could not ask. Say nothing rather than guess.
  if (dismissed || count === null || count >= 2) {
    return null;
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 border-b border-attention/40 bg-highlight-tint px-6 py-2 text-sm"
    >
      <span className="text-ink">
        You have one passkey. Add a second on another device in case you lose
        this one.
      </span>
      <a
        className={buttonClass("link")}
        href={accountUrl}
        target="_blank"
        rel="noreferrer"
      >
        Add a passkey
      </a>
      <span className="ml-auto">
        <Button
          variant="quiet"
          size="small"
          onClick={() => {
            remember();
            setDismissed(true);
          }}
        >
          Remind me later
        </Button>
      </span>
    </div>
  );
}
